import type { LucideIcon } from 'lucide-react';
import {
  MessageSquare, BookMarked, Eye,
  ScanSearch, Mic, ScanLine, TrendingUp,
  Wand2, Film, Sparkles, Clapperboard, PenTool, Paintbrush, Layers,
  BookOpen, Palette, Wrench,
} from 'lucide-react';

export type AppCategory = 'knowledge' | 'creativity' | 'tools';

// i18n (Task #76): name/description bleiben als Fallback, nameKey/descKey verweisen in den
// apps-Namespace (apps:registry.*). Aufloesung passiert in der konsumierenden Komponente
// (CategoryAppsPage.tsx via useTranslation), nicht hier - diese Datei ist reine Modul-Daten ohne
// Component-Scope. Gleiches Muster wie sidebarItems (Sidebar.tsx).
export interface AppDefinition {
  id: string;
  name: string;
  nameKey: string;
  description: string;
  descKey: string;
  icon: LucideIcon;
  route: string;
  category: AppCategory;
}

export interface CategoryDefinition {
  id: AppCategory;
  name: string;
  nameKey: string;
  icon: LucideIcon;
  description: string;
  descKey: string;
  route: string;
}

export const CATEGORIES: Record<AppCategory, CategoryDefinition> = {
  knowledge: {
    id: 'knowledge',
    name: 'Knowledge',
    nameKey: 'registry.category.knowledge.name',
    icon: BookOpen,
    description: 'AI assistants for research, analysis, and understanding',
    descKey: 'registry.category.knowledge.description',
    route: '/apps/knowledge',
  },
  creativity: {
    id: 'creativity',
    name: 'Creativity',
    nameKey: 'registry.category.creativity.name',
    icon: Palette,
    description: 'Generate and edit images and videos with AI',
    descKey: 'registry.category.creativity.description',
    route: '/apps/creativity',
  },
  tools: {
    id: 'tools',
    name: 'Tools',
    nameKey: 'registry.category.tools.name',
    icon: Wrench,
    description: 'Productivity tools to analyse and process content',
    descKey: 'registry.category.tools.description',
    route: '/apps/tools',
  },
};

export const APP_REGISTRY: AppDefinition[] = [
  {
    id: 'chat',
    name: 'Chat',
    nameKey: 'registry.app.chat.name',
    description: 'Conversational AI assistant for any topic',
    descKey: 'registry.app.chat.description',
    icon: MessageSquare,
    route: '/chat',
    category: 'knowledge',
  },
  {
    id: 'rag-bot',
    name: 'RAG Bot',
    nameKey: 'registry.app.ragBot.name',
    description: 'Chat with your own documents and knowledge base',
    descKey: 'registry.app.ragBot.description',
    icon: BookMarked,
    route: '/executions/10',
    category: 'knowledge',
  },
  {
    id: 'vision-model',
    name: 'Vision Model',
    nameKey: 'registry.app.visionModel.name',
    description: 'Analyse and describe image content with AI',
    descKey: 'registry.app.visionModel.description',
    icon: Eye,
    route: '/executions/5',
    category: 'knowledge',
  },
  {
    id: 'prompt-checker',
    name: 'Prompt Checker',
    nameKey: 'registry.app.promptChecker.name',
    description: 'Analyse and improve your AI prompts',
    descKey: 'registry.app.promptChecker.description',
    icon: ScanSearch,
    route: '/executions/8',
    category: 'tools',
  },
  {
    id: 'speech-to-text',
    name: 'Speech to Text',
    nameKey: 'registry.app.speechToText.name',
    description: 'Transcribe audio and voice recordings to text',
    descKey: 'registry.app.speechToText.description',
    icon: Mic,
    route: '/executions/11',
    category: 'tools',
  },
  {
    id: 'ocr-tool',
    name: 'OCR Tool',
    nameKey: 'registry.app.ocrTool.name',
    description: 'Extract text from documents and images',
    descKey: 'registry.app.ocrTool.description',
    icon: ScanLine,
    route: '/executions/4',
    category: 'tools',
  },
  {
    id: 'seo-checker',
    name: 'SEO Checker',
    nameKey: 'registry.app.seoChecker.name',
    description: 'Analyse and optimise content for search engines',
    descKey: 'registry.app.seoChecker.description',
    icon: TrendingUp,
    route: '/executions/9',
    category: 'tools',
  },
  {
    id: 'image-generation',
    name: 'Image Generation',
    nameKey: 'registry.app.imageGeneration.name',
    description: 'Create images from text prompts',
    descKey: 'registry.app.imageGeneration.description',
    icon: Wand2,
    route: '/executions/2',
    category: 'creativity',
  },
  {
    id: 'video-generation',
    name: 'Video Generation',
    nameKey: 'registry.app.videoGeneration.name',
    description: 'Generate short videos with AI',
    descKey: 'registry.app.videoGeneration.description',
    icon: Film,
    route: '/executions/3',
    category: 'creativity',
  },
  {
    id: 'image-to-gif',
    name: 'Image to GIF',
    nameKey: 'registry.app.imageToGif.name',
    description: 'Convert images into animated GIFs',
    descKey: 'registry.app.imageToGif.description',
    icon: Sparkles,
    route: '/executions/6',
    category: 'creativity',
  },
  {
    id: 'image-to-video',
    name: 'Image to Video',
    nameKey: 'registry.app.imageToVideo.name',
    description: 'Animate a still image into a video',
    descKey: 'registry.app.imageToVideo.description',
    icon: Clapperboard,
    route: '/executions/7',
    category: 'creativity',
  },
  {
    id: 'image-edit',
    name: 'Image Edit',
    nameKey: 'registry.app.imageEdit.name',
    description: 'Edit and transform images with AI assistance',
    descKey: 'registry.app.imageEdit.description',
    icon: PenTool,
    route: '/executions/12',
    category: 'creativity',
  },
  {
    id: 'image-inpaint',
    name: 'Image Inpaint',
    nameKey: 'registry.app.imageInpaint.name',
    description: 'Fill, remove, or replace parts of an image',
    descKey: 'registry.app.imageInpaint.description',
    icon: Paintbrush,
    route: '/executions/13',
    category: 'creativity',
  },
  {
    id: 'image-studio',
    name: 'Image Studio',
    nameKey: 'registry.app.imageStudio.name',
    description: 'Full-featured AI image editor with layers',
    descKey: 'registry.app.imageStudio.description',
    icon: Layers,
    route: '/executions/14',
    category: 'creativity',
  },
];

export function getAppsByCategory(category: AppCategory): AppDefinition[] {
  return APP_REGISTRY.filter(app => app.category === category);
}
